import { ReactNode, CSSProperties } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Colors, Typography } from '@zapier/design-system';
import {
  COLOR_KEYS,
  HeadingSize,
  HEADING_SIZE_MAP,
  mediaQueries,
  MEDIA_MIN_WIDTH,
  SUB_HEADING_SIZE_MAP,
} from '../../constants';
import {
  Background,
  isBackgroundStyle,
  mapBackgroundStyleToColor,
  isBackgroundImage,
  getBackgroundImage,
} from './background';
import { InViewTracking } from '../InViewTracking/InViewTracking';
import { SectionNameType } from '../../observability';

type ContentWidth = 'padded' | 'fullWidth' | 'overflow' | 'custom';

type Props = {
  /**
   * The width of the section determining the horizontal padding, in case 'custom' rely on inline styles
   */
  contentWidth?: ContentWidth;
  /**
   * Heading
   */
  heading?: string;
  headingSize?: HeadingSize;
  layerName: string | undefined;
  /**
   * Optional subheading
   */
  subheading?: string;
  /**
   * Subset of Zinnia colors allowed for section backgrounds or named background variation
   */
  background?: Background;
  /**
   * Section name used for tracking in component_scrolled_into_view events
   */
  sectionToTrack: SectionNameType;
  /**
   * The main content of the Section
   */
  children: ReactNode;
  /**
   * Custom styles, useful in conjunction with contentWidth 'custom'
   */
  style?: CSSProperties;
};

export function Section({
  background = 'transparent',
  children,
  contentWidth = 'padded',
  heading,
  headingSize,
  layerName,
  sectionToTrack,
  style = {},
  subheading,
}: Props) {
  return (
    <Outer
      as="section"
      background={background}
      contentWidth={contentWidth}
      layerName={layerName}
      sectionName={sectionToTrack}
      style={style}
    >
      <BackgroundLayer background={background} />
      {heading && (
        <HeadingsWrapper>
          {heading && (
            <Heading background={background} headingSize={headingSize}>
              {heading}
            </Heading>
          )}
          {subheading && (
            <Subheading
              headingSize={headingSize}
              background={background}
              css={css`
                margin-top: 10px;
                ${mediaQueries.tablet} {
                  ${Typography.paragraph1};
                  margin-top: 20px;
                }
              `}
            >
              {subheading}
            </Subheading>
          )}
        </HeadingsWrapper>
      )}
      <Content>{children}</Content>
    </Outer>
  );
}

const Outer = styled(InViewTracking, {
  shouldForwardProp: (prop) =>
    prop === 'as' || prop === 'sectionName' || prop === 'children',
})<{
  background: Background;
  contentWidth: ContentWidth;
  style: CSSProperties;
}>(
  ({ background, contentWidth }) => [
    isBackgroundStyle(background) &&
      css`
        background-color: ${Colors[mapBackgroundStyleToColor(background)]};
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      `,

    isBackgroundImage(background) &&
      css`
        background-image: url('/l/images/${getBackgroundImage(background)}');
        background-size: cover;
        background-position: center center;
        background-repeat: no-repeat;
      `,

    !isBackgroundStyle(background) &&
      !isBackgroundImage(background) && // In this case, only apply background color
      css`
        background-color: ${Colors[background]};
      `,

    contentWidth === 'padded' &&
      css`
        label: contentWidth-padded;
        padding: 40px 20px;
        ${mediaQueries.tablet} {
          padding: 60px 30px;
        }
        ${mediaQueries.desktop} {
          padding: 65px 100px;
        }
      `,

    contentWidth === 'fullWidth' &&
      css`
        label: contentWidth-fullWidth;
        padding: 50px 20px;
        ${mediaQueries.tablet} {
          padding: 70px 30px;
        }
      `,

    contentWidth === 'overflow' &&
      css`
        label: contentWidth-overflow;

        padding: 60px 0 60px 20px;
        ${mediaQueries.desktop} {
          padding: 65px 0 65px 30px;
        }
      `,

    contentWidth === 'custom' &&
      css`
        label: contentWidth-custom;
      `,
  ],
  ({ style }) => ({
    ...style,
  }),
);

const BackgroundLayer = styled.div<{
  background: Background;
}>(({ background }) => [
  isBackgroundStyle(background) &&
    css`
      align-items: center;
      display: flex;
      flex-direction: column;
      position: absolute;
      top: 0;
      height: 100%;
      width: 100%;
      max-width: 1350px;
      pointer-events: none;
    `,

  (background === 'Lava40Dotty' || background === 'Sun60Dotty') &&
    css`
      background-image: url('/l/images/dotty.png');
      background-size: cover;
      background-position: center center;
      background-repeat: no-repeat;
    `,
]);

const Content = styled.div`
  max-width: 1280px;
  margin: 0 auto 0 auto;
`;

/**
 * margin-bottom represents the spacing between the Heading(s) and Content
 */
const HeadingsWrapper = styled.div`
  margin-bottom: 40px;
  text-align: center;

  ${mediaQueries.tablet} {
    margin-bottom: 60px;
  }

  ${mediaQueries.desktop} {
    margin-bottom: 70px;
  }
`;

const Heading = styled.h2<{
  background: Background;
  headingSize: Props['headingSize'];
}>(({ background, headingSize }) =>
  css({
    maxWidth: '800px',
    margin: '0 auto',
    ...(headingSize
      ? Typography[HEADING_SIZE_MAP[headingSize].mobile]
      : Typography.sectionHeader1),
    color:
      background === COLOR_KEYS.zapier ||
      background === COLOR_KEYS.night ||
      background === COLOR_KEYS.moss ||
      background === COLOR_KEYS.neutral800 ||
      background === COLOR_KEYS.earth
        ? Colors.neutral100
        : Colors.neutral800,
    textAlign: 'center',
    '&:not(:last-child)': { marginBottom: '0.25em' },

    [MEDIA_MIN_WIDTH.small]: {
      ...(headingSize
        ? Typography[HEADING_SIZE_MAP[headingSize].tablet]
        : Typography.pageHeader5),
    },
    [MEDIA_MIN_WIDTH.large]: {
      ...(headingSize
        ? Typography[HEADING_SIZE_MAP[headingSize].desktop]
        : Typography.pageHeader5),
    },
  }),
);

/**
 * margin-top represents the spacing between Heading and Subheading
 */
const Subheading = styled.p<{
  background: Background;
  headingSize: Props['headingSize'];
}>(({ background, headingSize }) =>
  css({
    maxWidth: '800px',
    margin: '0 auto',
    ...(headingSize
      ? Typography[SUB_HEADING_SIZE_MAP[headingSize].mobile]
      : Typography.smallPrint1),
    color:
      background === COLOR_KEYS.zapier ||
      background === COLOR_KEYS.night ||
      background === COLOR_KEYS.moss ||
      background === COLOR_KEYS.neutral800 ||
      background === COLOR_KEYS.earth
        ? Colors.neutral100
        : Colors.neutral800,

    [MEDIA_MIN_WIDTH.small]: {
      ...(headingSize
        ? Typography[SUB_HEADING_SIZE_MAP[headingSize].tablet]
        : Typography.paragraph1),
    },
    [MEDIA_MIN_WIDTH.large]: {
      ...(headingSize
        ? Typography[SUB_HEADING_SIZE_MAP[headingSize].desktop]
        : Typography.paragraph1),
    },
  }),
);
